import { cashOutApis } from "@payping/api";
import { LeftOutlined, RedoOutlined } from "@payping/icons";
import { getBalance } from "@payping/redux/actions";
import { webConstants } from "@payping/utils/platformConstants";
import { Card, Space, Tooltip, Typography } from "@payping/web-components";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAsyncRetry } from "react-use";
import { addComma } from "@payping/utils/string";
import { useAppTranslation } from "@/utils/useAppTranslation";
import { balanceCardId, updateBalanceId } from "@/assets/googleTrackIds";
import { useAppDispatch, useAppSelector } from "@/utils/redux";
import styles from "./BalanceCard.module.scss";

const BalanceCard: React.FC = () => {
  const { t } = useAppTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const balance = useAppSelector((state) => state.withdraws.balance);
  const isSettlement = useAppSelector((state) => state.user.isSettlement);
  const [withdrawableBalance, setWithdrawableBalance] = useState<number>();
  const { overview } = useAppSelector((state) => state.cashOuts);

  const { loading, retry } = useAsyncRetry(async () => {
    if (isSettlement) {
      const overviewDetail = await cashOutApis.vversionCashOutOverviewGet("1");
      if (overviewDetail.withdrawableBalance !== undefined) setWithdrawableBalance(overviewDetail.withdrawableBalance);
    } else return dispatch(getBalance());
    return "";
  }, [dispatch, overview?.withdrawableBalance]);

  const onClick = (): void => {
    const url = webConstants.DashboardRoutes.user.requestWithdraw;
    navigate(url);
  };

  if (isSettlement)
    return (
      <Card.Raw
        clickable={true}
        background={"linear-gradient(301deg, #0080FF 0%, #2E97FF 100%)"}
        data-google-track={balanceCardId}
        onClick={() =>
          navigate({
            pathname: webConstants.DashboardRoutes.settlement.create,
          })
        }
      >
        <Card.Raw.ColMeta
          header={<Card.Raw.RowMeta title={t("Settlement:settlement.withdrawableBalance")}></Card.Raw.RowMeta>}
        >
          <Space>
            <Typography.Title level={1} color="white">
              {loading ? undefined : addComma(withdrawableBalance)}
            </Typography.Title>
            <Typography.Title level={3} color="white">
              {t("toman")}
            </Typography.Title>
          </Space>
        </Card.Raw.ColMeta>
        <Card.Raw.RowMeta showTopBorder title={t("overview.withdrawButton")}>
          <LeftOutlined height="15px" />
        </Card.Raw.RowMeta>
      </Card.Raw>
    );
  return (
    <Card.Raw
      clickable={Boolean(balance)}
      onClick={balance ? onClick : undefined}
      background={typeof balance === "number" ? "linear-gradient(301deg, #0080FF 0%, #2E97FF 100%)" : undefined}
      data-google-track={balanceCardId}
    >
      <Card.Raw.ColMeta
        header={
          <Card.Raw.RowMeta title={t("overview.balance")}>
            <Tooltip placement="bottomRight" title={t("overview.reloadBalance")}>
              <RedoOutlined
                width="15px"
                height="15px"
                onClick={(event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
                  event.stopPropagation();
                  retry();
                }}
                className={loading ? styles.updateBalance : undefined}
                data-google-track={updateBalanceId}
              />
            </Tooltip>
          </Card.Raw.RowMeta>
        }
      >
        <Space>
          <Typography.Title level={1} color="white">
            {loading ? undefined : addComma(balance)}
          </Typography.Title>
          <Typography.Title level={3} color="white">
            {t("toman")}
          </Typography.Title>
        </Space>
      </Card.Raw.ColMeta>
      {Boolean(balance) && (
        <Card.Raw.RowMeta showTopBorder title={t("overview.withdrawButton")}>
          <LeftOutlined height="15px" />
        </Card.Raw.RowMeta>
      )}
    </Card.Raw>
  );
};

export default BalanceCard;
